/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { useRef, useCallback } from "react";
import * as React from "react";
import { useCurrentUser } from "actions/CurrentUserActions";
import upgradeIcon from "components/shared/images/upgrade-icon.svg";
import accessIcon from "components/shared/images/premium-black-and-white.svg";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import {
  Analytics,
  AnalyticsEvent,
  ExpressCheckoutButtonPosition,
} from "constants/Analytics";
import {
  PurchaseExpressButton,
  UpgradeExpressButton,
} from "components/PurchaseExpress";
import { useLocation } from "react-router-dom";
import { CustomModal, CustomModalRefs } from "components/shared/CustomModal";
import { PremiumIcon } from "components/shared/PremiumIcon";
import {
  EnhancePremiumFeatureList,
  ExpressPremiumFeatureList,
  ProductList,
  StudioFeatureList,
  ProductFeatureList,
} from "components/Plans/ProductList";
import { IconMode } from "components/Plans/IconComponent";
import { ProductFeature } from "components/Plans/ProductFeature";
import { CallToAction } from "components/Plans/Plan";
import iconCheckGreen from "components/Plans/images/check-green.svg";
import premiumPreview1x from "images/EnhanceSpeech/premium-video-shorter.png";
import premiumPreview2x from "images/EnhanceSpeech/premium-video-shorter@2x.png";
import { ButtonIcon } from "components/shared/ButtonIcon";
import { AppPath } from "components/AppPath";

const PremiumUserContentScrollView = styled.div`
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PremiumUserContent: React.FC = () => {
  const isMobile = useIsMobile();

  const buttonSize = isMobile ? "xl" : "l";

  const products = [
    EnhancePremiumFeatureList,
    StudioFeatureList,
    ExpressPremiumFeatureList,
  ];

  return (
    <CustomModal>
      <CustomModal.Trigger>
        <overlay-trigger placement="bottom" slot="trigger">
          <sp-button
            size={buttonSize}
            variant="secondary"
            slot="trigger"
            data-testid="premium-access-button"
          >
            <ButtonIcon alt="" src={accessIcon} slot="icon" />
            {I18n.t("access_button.premium")}
          </sp-button>

          {
            // This is breaking on mobile.
            !isMobile && (
              <sp-tooltip slot="hover-content" open>
                {I18n.t("access_button.premium_tooltip")}
              </sp-tooltip>
            )
          }
        </overlay-trigger>
      </CustomModal.Trigger>
      <PremiumUserContentScrollView>
        <Title>
          {I18n.t("access_button.premium_tooltip")} <PremiumIcon />
        </Title>
        <Subtitle>{I18n.t("access_button.modal_subtitle")}</Subtitle>
        <ProductList
          products={products}
          mode={IconMode.dark}
          compact={isMobile}
        />
      </PremiumUserContentScrollView>
    </CustomModal>
  );
};

const DefaultImageContainer = styled.img`
  border: 2px solid #dee1e6;
`;

export const UpgradeButton: React.FC<{
  onClick?: VoidFunction;
  position: ExpressCheckoutButtonPosition;
  products?: ProductFeatureList[];
  productImage?: React.ReactNode;
}> = ({
  onClick = (): void => {},
  position,
  products = [
    EnhancePremiumFeatureList,
    StudioFeatureList,
    ExpressPremiumFeatureList,
  ],
  productImage = (
    <DefaultImageContainer
      srcSet={`${premiumPreview1x} 1x, ${premiumPreview2x} 2x`}
      alt=""
      aria-hidden="true"
    />
  ),
}) => {
  const { currentUser } = useCurrentUser();
  const isMobile = useIsMobile();
  const modalRef = useRef<CustomModalRefs>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const handleClosed = useCallback((): void => {
    triggerButtonRef.current?.focus();
  }, []);

  if (!currentUser) {
    return <></>;
  }

  if (currentUser.hasPremiumAccess) {
    return <PremiumUserContent />;
  }

  const buttonSize = isMobile ? "xl" : "l";

  const onUpgradeButtonClicked = (): void => {
    onClick();

    Analytics.instance.logEvent(AnalyticsEvent.USER_CLICKS_UPGRADE_BUTTON, {
      position: position,
    });
  };

  //TODO: This seems like a weird hack that could maybe just move into a FeaturesHeader component?
  if (location.pathname.endsWith(AppPath.PLANS)) {
    return (
      <UpgradeExpressButton
        color={"black"}
        position={
          isMobile
            ? ExpressCheckoutButtonPosition.FEATURES_PROFILE_DROPDOWN
            : ExpressCheckoutButtonPosition.FEATURES_HEADER
        }
        onClick={onUpgradeButtonClicked}
      >
        <ButtonIcon alt="" src={upgradeIcon} slot="icon" />
        {I18n.t("access_button.upgrade")}
      </UpgradeExpressButton>
    );
  }

  return (
    <CustomModal ref={modalRef} maxWidth={"1078px"}>
      <CustomModal.Trigger>
        <sp-button
          ref={triggerButtonRef}
          size={buttonSize}
          variant="primary"
          slot="trigger"
          treatment="fill"
          onClick={onUpgradeButtonClicked}
        >
          <ButtonIcon alt="" src={upgradeIcon} slot="icon" />
          {I18n.t("access_button.upgrade")}
        </sp-button>
      </CustomModal.Trigger>
      <Columns>
        <ImageContent>{productImage}</ImageContent>
        <TextContent>
          <Title>
            {I18n.t("plans.title")}
            &nbsp;
            <PremiumIcon />
          </Title>
          <ProductList
            products={products}
            mode={IconMode.dark}
            compact={isMobile}
          />
          <CallToAction data-testid="call-to-action">
            <PurchaseExpressButton
              color={"black"}
              position={position}
              onClick={(): void => modalRef.current?.close()}
              onClosed={handleClosed}
            >
              <PremiumIcon slot="icon" size={20} />
              {I18n.t("plans.premium.button")}
            </PurchaseExpressButton>
            <ProductFeature image={iconCheckGreen}>
              {I18n.t("plans.premium.reminder")}
            </ProductFeature>
          </CallToAction>
        </TextContent>
      </Columns>
    </CustomModal>
  );
};

const Title = styled.h1`
  font: normal normal 900 28px/30px ${fontFamilies};
  display: inline;
  gap: 8px;

  img {
    vertical-align: bottom;
  }
`;

const Subtitle = styled.div`
  font: normal normal normal 18px/24px ${fontFamilies};
`;

const ImageContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;

  img {
    object-fit: contain;
    max-width: 100%;
    border-radius: 12px;
  }

  ${devices.small}, ${devices.medium} {
    display: none;
  }
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Columns = styled.div`
  display: flex;
  gap: 64px;
  overflow-y: scroll;
`;
